import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import UploadFileForImages from "./UploadFileForImages";
import Gallery from "./Gallery";
import { fetchSecure } from "../utils/utils";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../index.css";

// helper from old code
function isImageExt(fieldData) {
  if (!fieldData || typeof fieldData !== "string") return false;
  const lower = fieldData.toLowerCase();
  return (
    lower.endsWith(".png") ||
    lower.endsWith(".gif") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".svg") ||
    lower.endsWith(".ico")
  );
}

// simple inline spinner
const InlineSpinner = ({ label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "16px 0",
    }}
  >
    <svg
      viewBox="0 0 50 50"
      width="34"
      height="34"
      style={{ display: "block" }}
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="rgba(252, 252, 252, 0.5)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="31.4 31.4"
        transform="rotate(-90 25 25)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="-90 25 25"
          to="270 25 25"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
    <div style={{ color: "#555", fontSize: 14 }}>{label || "Loading..."}</div>
  </div>
);

const PopupGallery = (props) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [savedImages, setSavedImages] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false); // <-- new

  const isBlog = props?.userDeep?.isBlog || false;
  console.log("PopupGallery :: isBlog", isBlog);

  // same defaults as old
  const defaultImages = [
    { src: "/Magic_wand.gif", type: "image" },
    { src: "/MUI-spinner.gif", type: "image" },
    { src: "/chatbot.png", type: "image" },
    { src: "/logo192.png", type: "image" },
    { src: "/robot.jpg", type: "image" },
  ];

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  /** -------------------------
   *  LOADER: support both flows
   *  -------------------------
   */
  useEffect(() => {
    async function loadLegacy() {
      setIsLoadingList(true); // start loader
      try {
        const fileData = await fetchSecure(
          "/api/getFileURLs/" + props.userDeep.id
        );
        let files = await fileData.json(); // [{src: "/api/getFile/<path>"}]
        files = files.map((f) => f.src);

        const unique = new Map();
        for (let file of files) {
          const index = file.indexOf(props.userDeep.id);
          if (index > -1) file = file.substring(index);

          const response = await fetchSecure("/api/getFileImage/" + file);
          const data = await response.json(); // [{src: "data:image/..."}]
          const image = data[0];
          image.fileName = file;
          image.type = "image";
          unique.set(image.src, image);
        }
        setSavedImages(Array.from(unique.values()));
        setLoaded(true);
      } finally {
        setIsLoadingList(false); // stop loader only after ALL have finished
      }
    }

    async function loadBlog() {
      setIsLoadingList(true); // start loader
      try {
        const resp = await fetchSecure(
          "/api/getBlogFileURLs/" + props.userDeep.id
        );
        const files = (await resp.json()) || []; // [{src, type: "image"|"video", contentType}]
        setSavedImages(files);
        setLoaded(true);
      } finally {
        setIsLoadingList(false); // stop loader
      }
    }

    if (loaded) return;
    if (isBlog) loadBlog();
    else loadLegacy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  /** -------------------------
   *  RELOAD: mirror the loader
   *  -------------------------
   */
  const reloadImages = async () => {
    if (isBlog) {
      setIsLoadingList(true);
      try {
        const resp = await fetchSecure(
          "/api/getBlogFileURLs/" + props.userDeep.id
        );
        const files = (await resp.json()) || [];
        setSavedImages(files); // keep type info
      } finally {
        setIsLoadingList(false);
      }
      return;
    }

    setIsLoadingList(true);
    try {
      const fileData = await fetchSecure(
        "/api/getFileURLs/" + props.userDeep.id
      );
      let files = await fileData.json();
      files = files.map((f) => f.src);

      const unique = new Map();
      for (let file of files) {
        const index = file.indexOf(props.userDeep.id);
        if (index > -1) file = file.substring(index);

        const response = await fetchSecure("/api/getFileImage/" + file);
        const data = await response.json();
        const image = data[0];
        image.fileName = file;
        image.type = "image";
        unique.set(image.src, image);
      }
      setSavedImages(Array.from(unique.values()));
    } finally {
      setIsLoadingList(false);
    }
  };

  /** -------------------------
   *  FILTER what to show based on allowVideo
   *  -------------------------
   */
  const displaySaved = useMemo(() => {
    if (!Array.isArray(savedImages)) return [];
    if (props.allowVideo) return savedImages.filter((m) => m.type === "video");
    return savedImages.filter((m) => m.type !== "video"); // images
  }, [savedImages, props.allowVideo]);

  /** -------------------------
   *  SELECT: keep old semantics, add blog shortcut
   *  -------------------------
   */
  const handleSelect = async (index, item /*, event */) => {
    if (typeof props.setFormData === "undefined") return;

    // Selecting from the "Sample Images" (defaultImages) case
    if (!isBlog && isImageExt(item)) {
      props.setFormData({
        data: {
          ...props.data,
          [props.field.name]: defaultImages[index].src.replace(/^\.\/+/, ""),
        },
        status: "IDLE",
      });
      setOpen(false);
      return;
    }

    // BLOG path: items are final URLs, use them directly
    if (isBlog) {
      const src = typeof item === "string" ? item : item?.src;
      if (!src) return;
      props.setFormData({
        data: { ...props.data, [props.field.name]: src },
        status: "IDLE",
      });
      setOpen(false);
      return;
    }

    // LEGACY path (images): replicate old behavior — refetch list and map by index
    const fileData = await fetchSecure("/api/getFileURLs/" + props.userDeep.id);
    let files = await fileData.json();
    files = files.map((f) => f.src); // array of /api/getFile/<path>

    props.setFormData({
      data: { ...props.data, [props.field.name]: files[index] },
      status: "IDLE",
    });
    setOpen(false);
  };
  // console.log("Props :: Pop up gallery :: props?.data ::", props?.data);

  return (
    <div className="gallery">
      <div
        className="small-primary-btn"
        onClick={handleClickOpen}
        style={{
          width: props.fromUserAdmin ? "10rem" : "auto",
          height: props.fromUserAdmin ? "3rem" : "auto",
          fontSize: props.fromUserAdmin ? "1rem" : "auto",
        }}
      >
        Open Gallery
      </div>
      <Dialog
        className="popup-gallery-dialog"
        disableScrollLock
        open={open}
        onClose={handleClose}
      >
        <div className="close-icon">
          <IconButton className="icon-button" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogTitle className="dialog-title">
          {props.allowVideo ? "Upload Video" : "Upload Image"}
        </DialogTitle>

        <UploadFileForImages
          filePrefix={props.userDeep.id.concat("/")}
          id={props.userDeep.id}
          user_id={props.userDeep.userId}
          callback={reloadImages}
          allData={props}
          allowVideo={props.allowVideo}
          isBlog={isBlog}
        />

        <DialogTitle className="dialog-title">
          {props.allowVideo ? "Saved Videos" : "Saved Images"}
        </DialogTitle>

        {/* loader while the list is being fetched */}
        {isLoadingList ? (
          <InlineSpinner label="Loading library…" />
        ) : (
          <Gallery
            images={displaySaved}
            handleSelect={handleSelect}
            canDelete={true}
            reloadImages={reloadImages}
            isBlog={isBlog}
            userDeep={props.userDeep}
          />
        )}

        <DialogTitle className="dialog-title">
          {props.allowVideo ? "Sample Videos" : "Sample Images"}
        </DialogTitle>

        <Gallery
          images={props.allowVideo ? [] : defaultImages}
          handleSelect={handleSelect}
          canDelete={false}
          reloadImages={reloadImages}
          isBlog={isBlog}
          userDeep={props.userDeep}
        />
      </Dialog>
    </div>
  );
};

export default PopupGallery;
